import { useState, useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import Modal from '../shared/Modal'
import { User, EditableMetric, MetricCategory } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  isOpen: boolean
  onClose: () => void
  metric: EditableMetric | null
  teamId: number
  teamMembers: User[]
  categories: MetricCategory[]
  // Set from the in-meeting scoreboard so the Inertia visit keeps the
  // meeting page's local component state (timers, in-progress edits).
  preserveState?: boolean
  // Restrict the follow-up reload to specific props (e.g. ['scoreboard_data'])
  // so an in-meeting save doesn't re-fetch the whole meeting page.
  only?: string[]
}

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]

const FORMAT_OPTIONS = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
]

const COMPARISON_OPTIONS = [
  { value: 'gte', label: '>= (Greater than or equal)' },
  { value: 'lte', label: '<= (Less than or equal)' },
  { value: 'gt', label: '> (Greater than)' },
  { value: 'lt', label: '< (Less than)' },
  { value: 'eq', label: '= (Equal to)' },
]

export default function EditMetricModal({ isOpen, onClose, metric, teamId, teamMembers, categories, preserveState = false, only }: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [frequency, setFrequency] = useState('weekly')
  const [format, setFormat] = useState('number')
  const [comparison, setComparison] = useState('gte')
  const [target, setTarget] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [decimalPlaces, setDecimalPlaces] = useState<number>(0)
  const [abbreviateLargeNumbers, setAbbreviateLargeNumbers] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize the form ONLY when the modal transitions to open for a given
  // metric — not on every render. Callers (especially the live meeting page,
  // which re-renders on heartbeat/presence polls) may recreate the `metric`
  // prop object each render; keying the effect on object identity would
  // silently reset the form mid-edit, so a subsequent Save would submit the
  // original values.
  const initializedForRef = useRef<number | null>(null)
  useEffect(() => {
    if (!isOpen) {
      initializedForRef.current = null
      return
    }
    if (metric && initializedForRef.current !== metric.id) {
      initializedForRef.current = metric.id
      setName(metric.name)
      setDescription(metric.description || '')
      setOwnerId(metric.owner ? String(metric.owner.id) : '')
      setFrequency(metric.frequency || 'weekly')
      setFormat(metric.format || 'number')
      setComparison(metric.comparison || 'gte')
      setTarget(metric.target != null ? String(metric.target) : '')
      setCategoryId(metric.category_id != null ? String(metric.category_id) : '')
      setDecimalPlaces(typeof metric.decimal_places === 'number' ? metric.decimal_places : 0)
      setAbbreviateLargeNumbers(metric.abbreviate_large_numbers ?? true)
      setError(null)
    }
  }, [isOpen, metric])

  if (!metric) return null

  const handleSave = () => {
    setSaving(true)
    setError(null)
    router.put(
      `/teams/${teamId}/scoreboard/metrics/${metric.id}`,
      {
        metric: {
          name,
          description: description || null,
          owner_id: ownerId || null,
          frequency,
          format,
          comparison,
          target: target ? parseFloat(target) : null,
          category_id: categoryId || null,
          decimal_places: decimalPlaces,
          abbreviate_large_numbers: abbreviateLargeNumbers,
        },
      },
      {
        preserveScroll: true,
        // Keep local component state (the edited form) when the server
        // responds with validation errors, so the modal stays open with the
        // user's changes intact. In-meeting callers pass preserveState=true
        // so the whole meeting page's local state survives the save.
        preserveState: preserveState || 'errors',
        ...(only ? { only } : {}),
        onError: errors => {
          const messages = Object.entries(errors).flatMap(([field, msgs]) =>
            [msgs].flat().filter(Boolean).map(msg => {
              const label = field.replace(/_/g, ' ')
              return `${label.charAt(0).toUpperCase()}${label.slice(1)} ${msg}`
            })
          )
          setError(messages.length > 0 ? messages.join(', ') : 'Could not save the metric. Please check the form and try again.')
        },
        onSuccess: () => {
          setError(null)
          onClose()
        },
        onFinish: () => {
          setSaving(false)
        },
      }
    )
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Metric">
      <div className="space-y-4">
        {error && (
          <div className="bg-status-off-track/10 border border-status-off-track/30 text-status-off-track text-sm rounded-lg px-3 py-2" role="alert">
            {error}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Weekly Revenue"
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={2}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Owner</label>
          <select
            value={ownerId}
            onChange={e => setOwnerId(e.target.value)}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
          >
            <option value="">Unassigned</option>
            {metric?.owner && !teamMembers.some(m => m.id === metric.owner?.id) && <option value={metric.owner.id} disabled>{metric.owner.full_name || `Person #${metric.owner.id}`} (Current owner)</option>}
            {sortPeople(teamMembers).map(m => (
              <option key={m.id} value={m.id}>{m.full_name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Frequency</label>
            <select
              value={frequency}
              onChange={e => setFrequency(e.target.value)}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              {FREQUENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Format</label>
            <select
              value={format}
              onChange={e => setFormat(e.target.value)}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              {FORMAT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Decimal places</label>
            <select
              value={decimalPlaces}
              onChange={e => setDecimalPlaces(parseInt(e.target.value, 10))}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              <option value={0}>0</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={abbreviateLargeNumbers}
                onChange={e => setAbbreviateLargeNumbers(e.target.checked)}
                className="w-4 h-4 rounded border-stone/30 text-pinnacle-red focus:ring-pinnacle-red/30"
              />
              Abbreviate large numbers
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Comparison</label>
            <select
              value={comparison}
              onChange={e => setComparison(e.target.value)}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
            >
              {COMPARISON_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Target</label>
            <input
              type="number"
              step="any"
              value={target}
              onChange={e => setTarget(e.target.value)}
              placeholder="e.g., 100"
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 focus:border-pinnacle-red"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Category</label>
          <select
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/30 bg-white"
          >
            <option value="">No Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-pinnacle-red hover:bg-pinnacle-red/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
